<template>
  <div id="middle">
      <vue-video-player
        v-if='this._videoSource'
        id="videoplayer"
        ref="videoPlayer"
        :playsinline="true"
        :options="playerOptions"
        :defaultPlaybackRate="speed"
        @timeupdate="timeChange"
        @canplay="canPlay"
        @loadeddata="loadedData"
        @ready="playerIsReady"
        @pause="playingHandler"
        @play="playingHandler"
        @ended="ended"
        @error="handleError"
        @ratechange="handleRateChange"
      />
      <owal-poster-frame-spinner
        v-else
        text="Loading Video..."
      />
  </div>
</template>
<script>

/*
The documentation for underlying video.js code is here:
- VJS: http://docs.videojs.com/docs/api/player.html
- VVP: https://github.com/dlive-io/vue-video-player

The component here lives at https://github.com/MosheAtOwal/vue-video-player. It's
a fork of a fork of the original, with video.js version 7.

VVP exports a component which we were previously installing globally in main.js.
Now, we install it here, as we do with many other components.

Also, I removed the HLS plugin (replaced with a newer plugin in VJS7) and
also the flash plugin, since Flash is dead™️. (I checked with Yakov first.)

// TODO: Skin the player with this: https://codepen.io/heff/pen/EarCt

*/
import 'video.js/dist/video-js.css'
import 'videojs-playlist'
import { videoPlayer as VueVideoPlayer } from 'vue-video-player'
import { playbackRates } from '@/libraries/controlpanel/playbackRates'
import { mapGetters, mapMutations } from 'vuex'
const OwalPosterFrameSpinner = () => import('@/libraries/owal-ui/spinners/OwalPosterFrameSpinner')

export default {
  name: 'owal-video-player',
  props: {
    videoSource: {
      type: String,
    },
    seekTime: {
      type: Number,
    },
    nextClipURL: {
      type: String,
    },
    playlist: {
      type: Array,
    },
  },
  components: {
    OwalPosterFrameSpinner,
    VueVideoPlayer,
  },
  data () {
    return {
      asynchronouslyTryingToPlay: false,
      useNextClip: false,
    }
  },
  watch: {
    isPlaying (newValue, oldValue) {
      if (!this.$refs.videoPlayer) {
        console.debug(``)
        return
      }
      if (newValue === false) {
        this.pause()
      } else {
        this.play()
      }
    },
    speed (newValue, oldValue) {
      this.updateVideoPlayerPlaybackRate(newValue)
    },
  },
  computed: {
    ...mapGetters({
      isLive: 'viewer/isLive',
      livestreamURL: 'viewer/livestreamURL',
      isPlaying: 'viewer/isPlaying',
      isFullscreen: 'viewer/isFullscreen',
      speed: 'viewer/playbackRate',
    }),
    playerOptions () {
      const options = {
        autoplay: true,
        preload: 'auto',
        muted: true,
        language: 'en',
        controls: true,
        playbackRates: playbackRates,
        flash: {
          hls: {
            withCredentials: false,
          },
        },
        html5: {
          hls: {
            withCredentials: false,
            autoplay: true,
          },
        },
        sources: [{
          withCredentials: false,
          type: this._mimeType,
          src: this._videoSource,
        }],
      }
      return options
    },
    /**
     * Controls the MIME type of the video.
     */
    _mimeType () {
      const isStreaming = this._videoSource === this.livestreamURL
      const type = isStreaming ? 'application/x-mpegURL' : 'video/mp4'
      // console.debug(`MIME Type: ${type}`)
      return type
    },
    /**
     * @returns The live stream URL or the source of the historical video.
     */
    _videoSource () {
      let source = this.livestreamURL || this.videoSource

      // console.debug(`Source: ${source}`)
      return source
    },
  },
  methods: {
    ...mapMutations({
      updatePlaying: 'viewer/setIsPlaying',
      updateRate: 'viewer/setPlaybackRate',
      updateFullScreen: 'viewer/setFullScreen',
    }),
    debugLive () {
      // console.debug(`Live: ${this.isLive}, URL Expired: ${this.liveStreamURL}`)
    },
    playerIsReady: function (player) {
      // console.log(`player is ready.`)
      // this.debugLive()
      // player.playlist()
    },
    loadedData: function () {
      this.videoPlayer().currentTime(this.seekTime)
    },
    canPlay: function () {
      this.play()
    },
    pause: function () {
      if (this.asynchronouslyTryingToPlay) {
        // console.log(`Can't pause while playing...`)
        return
      }
      this.videoPlayer().pause()
    },
    play: function () {
      const _playPromise = this.videoPlayer().play()
      if (!_playPromise) {
        return
      }
      // See https://developers.google.com/web/updates/2017/06/play-request-was-interrupted
      const self = this
      _playPromise.then(_ => {
        if (self) {
          self.asynchronouslyTryingToPlay = false
        }
      }).catch(error => {
        console.log(error)
        this.$emit('play-error', error)
      })
    },
    timeChange: function (event) {
      if (this.isLive) {
        return
      }
      this.$emit('time-update', this.videoPlayer().currentTime())
    },
    videoPlayer: function () {
      const component = this.$refs.videoPlayer
      if (!component) {
        return undefined
      }
      return component.player
    },
    updateVideoPlayerPlaybackRate (playbackRate) {
      if (!this.videoPlayer()) {
        return
      }

      this.videoPlayer().playbackRate(playbackRate)
    },
    setLongClip: function () {
      this.$store.commit('setLongClip', true)
    },
    longClip: function () {
    // code
    },
    fullscreen: function () {
    // figure out a way to exit before hiding the native controls
      if (this.isFullscreen === true) {
        this.videoPlayer().requestFullscreen()
      }
      this.updateFullScreen(false)
    },
    ended (player) {
      // console.debug(`Ended Next URL: ${this.nextClipURL}`)
      this.$emit('ended')
      player.src({ type: this._mimeType, src: this.nextClipURL })
      this.updatePlaying(true)
      const speed = this.speed
      this.updateVideoPlayerPlaybackRate(speed)
    },
    playingHandler (player) {
      const playing = !player.paused()
      this.updatePlaying(playing)
    },
    handleRateChange (player) {
      // const rate = player.rate
      // this.updateRate(rate)
    },
    handleError (error) {
      this.$ga.event({
        eventCategory: 'video',
        eventAction: 'error',
        eventLabel: error.message,
        eventValue: 123,
      })
    },
  },
  mounted () {
    this.updatePlaying(true)
    const speed = this.speed
    this.updateVideoPlayerPlaybackRate(speed)
  },
}
</script>

<style scoped>
#videoplayer {
  flex: auto;
  height: 100%;
  width: 100%;
  background-color: #000;
}

#videoplayer:-webkit-full-screen {
  width: 100%;
  height: 100%;
}

.ui.compact.buttons {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

button {
  flex: initial;
}

#middle {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.button.is-active + .icon {
  height: auto;
  opacity: 1;
  visibility: visible;
}

.button-row {
  flex: initial;
  width: 100%;
  height: initial;
}

.button-flex-container{
  display: flex;
  flex-direction: row;
  justify-content: center;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-leave,
.fade-enter-to {
  opacity: 1;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms ease;
}

</style>

<style>

.video-js {
  width: 100%;
  height: 100%;
}
</style>
